"use client";

import { ArrowLeft, ArrowRight } from "lucide-react";
import { useState, useEffect, useRef } from "react";

import { Button } from "@/components/ui/button";
import {
  Carousel,
  CarouselApi,
  CarouselContent,
  CarouselItem,
} from "@/components/ui/carousel";
import { OptimizedImage } from "@/components/OptimizedImage";
import { useLanguage } from "@/contexts/LanguageContext";
import { getLocalizedUrl } from "@/utils/languageUtils";
import { getHomeBlurPlaceholder } from "@/utils/homeBlurPlaceholders";

export interface RestaurantGalleryItem {
  id: string;
  title: string;
  description: string;
  href: string;
  image: string;
  category:
    | "Fine Dining"
    | "Alpine Cuisine"
    | "Swiss Specialties"
    | "Wine Selection"
    | "Desserts"
    | "Breakfast"
    | "Ambiance";
}

export interface RestaurantGalleryCarouselProps {
  title?: string;
  description?: string;
  items: RestaurantGalleryItem[];
}

const restaurantData: RestaurantGalleryItem[] = [
  {
    id: "swiss-specialties-2",
    title: "Rösti & Local Delicacies",
    description:
      "Classic Swiss rösti and regional specialties prepared with time-honored techniques.",
    href: "/dining",
    image:
      "/images/Homepage Shortlisted media/Gallery 02 Restaurant-Food/IMG_5196.optimized.webp",
    category: "Swiss Specialties",
  },
  {
    id: "desserts-1",
    title: "Alpine Desserts",
    description:
      "Decadent desserts inspired by Swiss mountain traditions, featuring local berries and chocolate.",
    href: "/dining",
    image:
      "/images/Homepage Shortlisted media/Gallery 02 Restaurant-Food/IMG_5363.optimized.webp",
    category: "Desserts",
  },
  {
    id: "fine-dining-1",
    title: "Fine Dining Experience",
    description:
      "Exquisite culinary artistry featuring locally-sourced ingredients and innovative Alpine cuisine.",
    href: "/dining",
    image:
      "/images/Homepage Shortlisted media/Gallery 02 Restaurant-Food/IMG_5140.optimized.webp",
    category: "Fine Dining",
  },
  {
    id: "ambiance-4",
    title: "Alpine Dining Experience",
    description:
      "Experience the perfect blend of Swiss hospitality and mountain charm in our elegant dining space.",
    href: "/dining",
    image:
      "/images/Homepage Shortlisted media/Gallery 02 Restaurant-Food/IMG_6766.optimized.webp",
    category: "Ambiance",
  },
  {
    id: "breakfast-1",
    title: "Mountain Breakfast",
    description:
      "Hearty breakfast spread featuring Swiss specialties, fresh pastries, and mountain views.",
    href: "/dining",
    image:
      "/images/Homepage Shortlisted media/Gallery 02 Restaurant-Food/IMG_6394.optimized.webp",
    category: "Breakfast",
  },
  {
    id: "fine-dining-3",
    title: "Culinary Excellence",
    description:
      "Award-winning chefs create memorable dining experiences with seasonal Alpine ingredients.",
    href: "/dining",
    image:
      "/images/Homepage Shortlisted media/Gallery 02 Restaurant-Food/IMG_5178.optimized.webp",
    category: "Fine Dining",
  },
  {
    id: "wine-selection-2",
    title: "Sommelier's Choice",
    description:
      "Expert wine pairings selected by our sommelier to enhance your dining experience.",
    href: "/dining",
    image:
      "/images/Homepage Shortlisted media/Gallery 02 Restaurant-Food/IMG_5265.optimized.webp",
    category: "Wine Selection",
  },
  {
    id: "swiss-specialties-1",
    title: "Swiss Cheese Fondue",
    description:
      "Traditional Swiss fondue made with premium local cheeses, served in authentic Alpine style.",
    href: "/dining",
    image:
      "/images/Homepage Shortlisted media/Gallery 02 Restaurant-Food/IMG_5195.optimized.webp",
    category: "Swiss Specialties",
  },
  {
    id: "desserts-2",
    title: "Sweet Swiss Treats",
    description:
      "Traditional Swiss confections and modern dessert creations to complete your meal.",
    href: "/dining",
    image:
      "/images/Homepage Shortlisted media/Gallery 02 Restaurant-Food/IMG_5403.optimized.webp",
    category: "Desserts",
  },
  {
    id: "fine-dining-2",
    title: "Gourmet Presentation",
    description:
      "Meticulously crafted dishes that celebrate the rich flavors of Swiss mountain cuisine.",
    href: "/dining",
    image:
      "/images/Homepage Shortlisted media/Gallery 02 Restaurant-Food/IMG_5177.optimized.webp",
    category: "Fine Dining",
  },
  {
    id: "alpine-cuisine-1",
    title: "Traditional Alpine Dishes",
    description:
      "Authentic mountain recipes passed down through generations, prepared with local ingredients.",
    href: "/dining",
    image:
      "/images/Homepage Shortlisted media/Gallery 02 Restaurant-Food/IMG_5192.optimized.webp",
    category: "Alpine Cuisine",
  },
  {
    id: "swiss-specialties-3",
    title: "Artisanal Swiss Cuisine",
    description:
      "Handcrafted dishes showcasing the finest Swiss culinary traditions and local produce.",
    href: "/dining",
    image:
      "/images/Homepage Shortlisted media/Gallery 02 Restaurant-Food/IMG_5263.optimized.webp",
    category: "Swiss Specialties",
  },
  {
    id: "ambiance-3",
    title: "Mountain Views",
    description:
      "Dine with breathtaking views of the Bernese Alps from our panoramic restaurant windows.",
    href: "/dining",
    image:
      "/images/Homepage Shortlisted media/Gallery 02 Restaurant-Food/IMG_6713.optimized.webp",
    category: "Ambiance",
  },
];

const RestaurantGalleryCarousel = ({
  title,
  description,
  items = restaurantData,
}: RestaurantGalleryCarouselProps) => {
  // Get language and translation function
  const { t, language } = useLanguage();
  const [carouselApi, setCarouselApi] = useState<CarouselApi>();
  const [canScrollPrev, setCanScrollPrev] = useState(false);
  const [canScrollNext, setCanScrollNext] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [currentSlideGroup, setCurrentSlideGroup] = useState(0);
  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    if (!carouselApi) {
      return;
    }

    const updateSelection = () => {
      setCanScrollPrev(carouselApi.canScrollPrev());
      setCanScrollNext(carouselApi.canScrollNext());
      const selectedSlide = carouselApi.selectedScrollSnap();
      setCurrentSlide(selectedSlide);
      // Calculate which group of 3 slides is currently visible
      setCurrentSlideGroup(Math.floor(selectedSlide / 3));
    };

    updateSelection();
    carouselApi.on("select", updateSelection);
    carouselApi.on("reInit", updateSelection);

    return () => {
      carouselApi.off("select", updateSelection);
      carouselApi.off("reInit", updateSelection);
    };
  }, [carouselApi]);

  // Auto progress functionality
  useEffect(() => {
    if (!carouselApi || isHovered) return;

    const interval = setInterval(() => {
      if (carouselApi.canScrollNext()) {
        carouselApi.scrollNext();
      } else {
        carouselApi.scrollTo(0); // Reset to first slide
      }
    }, 4000); // Change slide every 4 seconds

    return () => clearInterval(interval);
  }, [carouselApi, isHovered]);

  // Video intersection observer - play when 70% in view
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && entry.intersectionRatio >= 0.7) {
            video.play().catch(console.error);
          } else {
            video.pause();
          }
        });
      },
      {
        threshold: 0.7, // Trigger when 70% of the video is visible
        rootMargin: "0px",
      },
    );

    observer.observe(video);

    return () => {
      observer.unobserve(video);
    };
  }, []);

  // Safari video autoplay fix - Programmatic play trigger
  // Note: IntersectionObserver handles play/pause based on visibility
  // This ensures Safari can autoplay by attempting play() when metadata loads
  // IntersectionObserver will manage actual playback based on visibility
  useEffect(() => {
    const playVideo = async () => {
      if (!videoRef.current) return;

      try {
        // Always try to play when metadata loads (helps Safari autoplay policy)
        // IntersectionObserver will pause if not visible, which may cause AbortError (expected)
        await videoRef.current.play();
      } catch (error) {
        // Ignore AbortError - IntersectionObserver paused it (expected behavior)
        // Also ignore NotAllowedError - Safari's autoplay policy (IntersectionObserver will handle it)
        if (
          error instanceof DOMException &&
          error.name !== "AbortError" &&
          error.name !== "NotAllowedError"
        ) {
          console.log("Video autoplay prevented:", error);
        }
      }
    };

    // Try when video metadata is loaded (important for Safari autoplay policy)
    const video = videoRef.current;
    if (video) {
      video.addEventListener("loadedmetadata", playVideo);
      video.addEventListener("canplay", playVideo);
      return () => {
        video.removeEventListener("loadedmetadata", playVideo);
        video.removeEventListener("canplay", playVideo);
      };
    }
  }, []);

  return (
    <section
      className="py-20 bg-gradient-to-b from-elegant-white via-pebble-beige to-elegant-white"
      data-oid="5v843jj"
    >
      <div
        className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8"
        data-oid="-h6phzk"
      >
        {/* Mobile Layout */}
        <div className="lg:hidden" data-oid="vdxeu2h">
          {/* Section Header for Mobile */}
          <div className="text-center mb-12" data-oid="qxq1d8i">
            <h2
              className="text-4xl md:text-5xl text-gray-900 font-playfair mb-6 uppercase"
              data-oid="n61:-u5"
            >
              {title || t("restaurant.title")}
            </h2>
            <p
              className="text-lg text-gray-900 font-lato max-w-4xl mx-auto leading-relaxed mb-8"
              data-oid="o2faimu"
            >
              {description || t("restaurant.description")}
            </p>

            {/* Jungfrau Logo */}
            <div className="flex justify-center" data-oid="zhh1gkv">
              <img
                src="/images/jungfrau dark.webp"
                alt="Jungfrau Region Logo"
                className="h-32 w-auto object-contain"
                data-oid="bg5ftoc"
              />
            </div>
          </div>

          {/* Video Section with Button Overlay */}
          <div className="mb-12" data-oid="unwgh43">
            <div
              className="relative w-full aspect-[9/16] overflow-hidden rounded-xl"
              data-oid="ct1vgs2"
            >
              <video
                ref={videoRef}
                className="w-full h-full object-cover"
                autoPlay
                muted
                loop
                playsInline
                preload="metadata"
                controls={false}
                data-oid="r-vvl-t"
              >
                <source
                  src="/videos/home-rest-optimized.mp4"
                  type="video/mp4"
                  data-oid="v5m451n"
                />
                Your browser does not support the video tag.
              </video>
              {/* Gradient overlay for better button visibility */}
              <div
                className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"
                data-oid="69.1pkh"
              ></div>
              {/* Explore Restaurant Button Overlay at Bottom */}
              <div
                className="absolute bottom-0 left-0 right-0 flex items-end justify-center pb-8 z-10"
                data-oid="rjzio0n"
              >
                <a
                  href={getLocalizedUrl("/dining", language)}
                  className="group"
                  data-oid="hs27k1x"
                >
                  <Button
                    className="bg-alpine-gold hover:bg-alpine-gold/90 text-white px-8 py-3 rounded-full font-lato font-medium shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105"
                    data-oid="vvz6aq_"
                  >
                    {t("restaurant.exploreRestaurant")}
                    <ArrowRight
                      className="ml-2 size-4 transition-transform group-hover:translate-x-1"
                      data-oid="l5vo8uu"
                    />
                  </Button>
                </a>
              </div>
            </div>
          </div>

          {/* Basic Carousel */}
          <div
            className="relative"
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
            data-oid="ythynp8"
          >
            <Carousel
              setApi={setCarouselApi}
              opts={{
                align: "start",
                loop: false,
              }}
              className="w-full"
              data-oid="y8axmxf"
            >
              <CarouselContent data-oid="tiu:6xv">
                {items.map((item) => (
                  <CarouselItem
                    key={item.id}
                    className="md:basis-1/2 lg:basis-1/3"
                    data-oid="z8daqgg"
                  >
                    <a
                      href={getLocalizedUrl(item.href, language)}
                      className="group rounded-xl"
                      data-oid="-tknlq_"
                    >
                      <div
                        className="group relative h-64 overflow-hidden rounded-xl"
                        data-oid="u3ikrjy"
                      >
                        <OptimizedImage
                          src={item.image}
                          alt={item.title}
                          className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-110"
                          priority={false}
                          fetchPriority="auto"
                          blurDataURL={getHomeBlurPlaceholder(item.image)}
                          data-oid="qoh0z:0"
                        />

                        {/* Gradient overlay for button visibility */}
                        <div
                          className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"
                          data-oid="igu1xy1"
                        />

                        {/* Explore link at bottom */}
                        <div
                          className="absolute bottom-4 left-4 right-4 text-elegant-white"
                          data-oid="ampj:-l"
                        >
                          <div
                            className="flex items-center text-sm font-lato font-medium text-elegant-white/90 hover:text-alpine-gold transition-colors duration-300"
                            data-oid="-ua4-ef"
                          >
                            {t("restaurant.exploreRestaurant")}{" "}
                            <ArrowRight
                              className="ml-2 size-4 transition-transform group-hover:translate-x-1"
                              data-oid="9x7pnsb"
                            />
                          </div>
                        </div>
                      </div>
                    </a>
                  </CarouselItem>
                ))}
              </CarouselContent>
            </Carousel>

            {/* Pagination Dots - Mobile/Tablet Only */}
            <div className="flex justify-center gap-2 mt-4" data-oid="vc-5q7p">
              {Array.from({ length: Math.ceil(items.length / 3) }).map(
                (_, index) => (
                  <button
                    key={index}
                    onClick={() => carouselApi?.scrollTo(index * 3)}
                    className={`w-2 h-2 rounded-full transition-all duration-300 ${
                      currentSlideGroup === index
                        ? "bg-alpine-gold scale-125"
                        : "bg-gray-300 hover:bg-gray-400"
                    }`}
                    aria-label={`Go to slide group ${index + 1}`}
                    data-oid="ux1x6ac"
                  />
                ),
              )}
            </div>
          </div>
        </div>

        {/* Desktop Layout - Bento Box Grid */}
        <div
          className="hidden lg:grid lg:grid-cols-[420px_1fr] lg:gap-4 lg:items-stretch"
          data-oid="3dohdne"
        >
          {/* Left Column: Video */}
          <div
            className="relative w-full aspect-[9/16] overflow-hidden rounded-xl"
            data-oid="5jz-vfr"
          >
            <video
              ref={videoRef}
              className="w-full h-full object-cover"
              autoPlay
              muted
              loop
              playsInline
              preload="metadata"
              controls={false}
              data-oid="wd0hqe3"
            >
              <source
                src="/videos/home-rest-optimized.mp4"
                type="video/mp4"
                data-oid="jdxf9.c"
              />
              Your browser does not support the video tag.
            </video>
          </div>

          {/* Right Column: Text Content and Carousel */}
          <div
            className="flex flex-col justify-between h-full"
            data-oid=":5_4pza"
          >
            {/* Text Content in the "nook" */}
            <div
              className="flex-1 flex flex-col justify-center px-6"
              data-oid="0u207wx"
            >
              <h2
                className="text-4xl lg:text-5xl text-gray-900 font-playfair mb-6 uppercase"
                data-oid="kxy5w35"
              >
                {title || t("restaurant.title")}
              </h2>
              <p
                className="text-lg text-gray-900 font-lato leading-relaxed mb-8"
                data-oid="0lx4tp-"
              >
                {description || t("restaurant.description")}
              </p>

              {/* Jungfrau Logo */}
              <div className="flex justify-center" data-oid="2skjxu0">
                <img
                  src="/images/jungfrau dark.webp"
                  alt="Jungfrau Region Logo"
                  className="h-32 w-auto object-contain"
                  data-oid="ahh-ru4"
                />
              </div>
            </div>
            {/* Pagination Controls - Contained */}
            <div className="mb-3 flex gap-2" data-oid="-dmq859">
              <Button
                size="icon"
                variant="outline"
                onClick={() => carouselApi?.scrollPrev()}
                disabled={!canScrollPrev}
                className="border border-gray-300 hover:border-gray-400 rounded-lg"
                data-oid="v3639j-"
              >
                <ArrowLeft className="h-4 w-4" data-oid=".gbxodn" />
              </Button>
              <Button
                size="icon"
                variant="outline"
                onClick={() => carouselApi?.scrollNext()}
                disabled={!canScrollNext}
                className="border border-gray-300 hover:border-gray-400 rounded-lg"
                data-oid="2ok3e.r"
              >
                <ArrowRight className="h-4 w-4" data-oid="i.qnsh7" />
              </Button>
            </div>

            {/* Carousel - Fixed height for uniform cards */}
            <div
              className="relative h-[400px]"
              onMouseEnter={() => setIsHovered(true)}
              onMouseLeave={() => setIsHovered(false)}
              data-oid="467z5f8"
            >
              <Carousel
                setApi={setCarouselApi}
                opts={{
                  align: "start",
                  loop: false,
                }}
                className="w-full h-full"
                data-oid="qjh40fc"
              >
                <CarouselContent className="h-full" data-oid="wl82tup">
                  {items.map((item) => (
                    <CarouselItem
                      key={item.id}
                      className="basis-1/3 h-[400px]"
                      data-oid="-aziwkj"
                    >
                      <a
                        href={getLocalizedUrl(item.href, language)}
                        className="group rounded-xl"
                        data-oid="-3ho_.4"
                      >
                        <div
                          className="group relative h-[400px] w-full overflow-hidden rounded-xl"
                          data-oid="n7vjki5"
                        >
                          <OptimizedImage
                            src={item.image}
                            alt={item.title}
                            className="h-full w-full object-cover object-center transition-transform duration-500 group-hover:scale-105"
                            priority={false}
                            fetchPriority="auto"
                            blurDataURL={getHomeBlurPlaceholder(item.image)}
                            data-oid="5b2qyly"
                          />

                          {/* Gradient overlay for button visibility */}
                          <div
                            className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"
                            data-oid="5txethg"
                          />

                          <div
                            className="absolute inset-x-0 bottom-0 flex flex-col items-start p-6 text-elegant-white"
                            data-oid="6g64xfq"
                          >
                            <div
                              className="flex items-center text-sm font-lato font-medium group-hover:text-alpine-gold transition-colors duration-300"
                              data-oid="t_oo.jh"
                            >
                              {t("restaurant.exploreRestaurant")}{" "}
                              <ArrowRight
                                className="ml-2 size-4 transition-transform group-hover:translate-x-1"
                                data-oid="z2l8imd"
                              />
                            </div>
                          </div>
                        </div>
                      </a>
                    </CarouselItem>
                  ))}
                </CarouselContent>
              </Carousel>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export { RestaurantGalleryCarousel };
